import { ColumnsType } from "antd/es/table";
import { FILTER_FEATURE } from "api/ApiPath";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterAttributes } from "components/generalComponents/filterComponents/model/FilterComponentsModel";
import { PartnerResponseEntity } from "features/generals/domain/entities/partners/PartnerResponseEntity";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { FeatureFormat } from "utils/format/FeatureFormat";
import { checkLowerCase } from "utils/format/StringFormat";
import { formatCurrency } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { GeneralRowType } from "../interfaces/IGenerals";
import { GeneralCurrencyMappers } from "../mappers/GeneralCurrencyMappers";

export class GeneralConstants {
  static buildColumns = (
    periodLabels: string[],
    currencyCode: string,
    valueType: "currency" | "number" = "currency"
  ): ColumnsType<GeneralRowType> => {
    const dynamicCols: ColumnsType<GeneralRowType> = periodLabels.map(
      (label) => ({
        title: label,
        dataIndex: label,
        align: "right",
        width: 200,
        render: (val: any) =>
          valueType === "currency"
            ? formatCurrency(
                Number(val || 0),
                true,
                true,
                GeneralCurrencyMappers.getCurrencyEnum(currencyCode)
              )
            : Number(val || 0).toLocaleString(),
      })
    );

    return [
      {
        title: strings.General.Catalogue,
        dataIndex: "name",
        fixed: "left",
        width: 150,
      },
      ...dynamicCols,
    ];
  };

  static filters(
    localString: typeof strings,
    partnerList: Array<PartnerResponseEntity>,
    carrierList: Array<BaseFilterListResponseModel>,
    currencyOptions: Array<{ label?: string; value?: any }> = [
      { label: "VND", value: "VND" },
    ],
    handlePartnerChange?: (partnerCode: string) => void,
    storeOptions: Array<{ label?: string; value?: any }> = [],
    staffOptions: Array<{ label?: string; value?: any }> = [],
    selectedPartnerType?: number,
    isStore?: boolean,
    isAllStore?: boolean,
    handleStoreChange?: (storeCode: string) => void,
    handleStaffChange?: (staff: string[]) => void,
    staffLoading = false
  ) {
    const hasExtendedFilters = selectedPartnerType === 1 && isStore;
    const isAbleToSeePartners =  FeatureFormat.findFeature(FILTER_FEATURE.PARTNER_LIST);
    const filters = [
      {
        colLg: hasExtendedFilters ? 3 : 4,
        colDefault: 8,
        defaultValue: "VND",
        filterName: "currency",
        filterType: "select",
        labelName: localString.General.ByCurrency,
        placeholder: localString.General.ByCurrency,
        options: currencyOptions,
      },
      isAbleToSeePartners
        ? {
            colLg: hasExtendedFilters ? 6 : 10,
            colDefault: 8,
            defaultValue: "",
            filterName: "partner",
            filterType: "select",
            labelName: localString.GlobalLabels.Partner,
            placeholder: localString.GlobalPlaceholder.Partner,
            customKey: ["email", "phone"],
            needCapaChecking: true,
            options: [
              {
                label: localString.GlobalLabels.All,
                value: "",
              },
              ...partnerList?.map((item) => ({
                label: `${checkLowerCase(
                  item?.name
                )}-${item?.providerCode?.toUpperCase()}`,
                value:
                  isString(item?.partnerRefCode) &&
                  !isEmpty(item?.partnerRefCode)
                    ? item?.partnerRefCode
                    : item?.code,
                email: item?.email,
                phone: item?.phone,
                type: item?.type,
                isStore: item?.isStore,
                providerCode: item?.providerCode,
                code: item?.code,
              })),
            ],
            onChange: handlePartnerChange,
          }
        : undefined,
      {
        colLg: hasExtendedFilters ? 5 : 10,
        colDefault: 8,
        defaultValue: [""],
        filterName: "telco",
        filterType: "select-multiple",
        needCapaChecking: true,
        labelName: localString.GlobalLabels.Carrier,
        placeholder: localString.GlobalLabels.Carrier,
        options: [
          {
            label: localString.GlobalLabels.All,
            value: "",
          },
          ...carrierList?.map((item) => ({
            label: item?.name,
            value: item?.code,
          })),
        ],
      },
    ]?.filter((child) => !isUndefined(child)) as Array<FilterAttributes>;

    if (hasExtendedFilters) {
      filters.splice(
        3,
        0,
        {
          colLg: 6,
          colDefault: 8,
          defaultValue: "",
          filterName: "store",
          filterType: "select",
          labelName: localString.GlobalLabels.Store,
          capabilityKey: CapabilityEnum.storeFilter,
          needCapaChecking: true,
          options: storeOptions,
          onChange: handleStoreChange,
          placeholder: localString.GlobalLabels.Choose,
        } as FilterAttributes,
        {
          colLg: 4,
          colDefault: 8,
          defaultValue: [""],
          filterName: "staff",
          filterType: "select-multiple",
          labelName: localString.GlobalLabels.Staff,
          capabilityKey: CapabilityEnum.staffFilter,
          needCapaChecking: true,
          options: staffOptions,
          disable: isAllStore,
          loading: staffLoading,
          onChange: handleStaffChange,
          placeholder: localString.GlobalLabels.Choose,
        } as FilterAttributes
      );
    }

    return filters;
  }
}
